import { useEffect, useMemo } from 'react';

import { nanoid } from 'nanoid';
import useWebSocket from 'react-use-websocket';

import { WS_URL } from '@/shared/api/constants';
import { getDecodedStartappQuery } from '@/shared/helpers';
import { parseJSON } from '@/shared/helpers/parse-json';
import { type URL_SOURCES } from '@/shared/services/analytics/types';

import { useAppActions, useAppState } from '../../store/app-store/app-store';
import { INIT_WS_OPTIONS } from './constants';
import { logMessage, makeMessage } from './helpers';
import { type CbType, type Message, MessageAction, MessageType } from './types';

export const useWs = () => {
  const callbackStore = useMemo(() => new Map<MessageType, Array<CbType>>(), []);

  const { token, marketSessionId, wsSentInitialMsg } = useAppState();
  const { hydrate } = useAppActions();

  const {
    sendMessage: sendMessageLib,
    lastJsonMessage,
    lastMessage,
    getWebSocket,
    readyState,
  } = useWebSocket<Message>(import.meta.env.VITE_APP_WS_URL || WS_URL, {
    ...INIT_WS_OPTIONS,
    onOpen: () => {
      if (!wsSentInitialMsg) {
        registerSocketConnection();
        hydrate({ wsSentInitialMsg: true });
      }
    },
    onClose: () => {
      hydrate({ wsSentInitialMsg: false });
    },
  });

  const sendMessage = (type: MessageType, action: MessageAction, payload: any = {}) => {
    logMessage(type, { action, payload });

    sendMessageLib(makeMessage(type, action, payload));
  };

  const subscribe = (type: MessageType, cb?: CbType) => {
    const uuid = nanoid();

    sendMessage(type, MessageAction.SUBSCRIBE, { subscription_id: uuid });
    cb && manageCbs(type, cb);

    return uuid;
  };

  const unsubscribe = (type: MessageType, id: string) => {
    sendMessage(type, MessageAction.UNSUBSCRIBE, { subscription_id: id });
  };

  const manageCbs = (type: MessageType, cb: CbType) => {
    const typeCbs = callbackStore.has(type);
    if (typeCbs) {
      const arr = callbackStore.get(type);
      if (arr) callbackStore.set(type, [...arr, cb]);
    } else {
      callbackStore.set(type, [cb]);
    }
  };

  const registerSocketConnection = () => {
    let payload: {
      token: string;
      market_session_id: string;
      ref?: string;
      src?: URL_SOURCES;
      labels?: string[];
    } = {
      token,
      market_session_id: marketSessionId,
    };

    const decodedStartapp = getDecodedStartappQuery();

    if (decodedStartapp.ref) {
      payload = { ...payload, ref: decodedStartapp.ref };
    }

    if (decodedStartapp.src) {
      payload = { ...payload, src: decodedStartapp.src };
    }

    if (decodedStartapp.labels?.length) {
      payload = { ...payload, labels: decodedStartapp.labels };
    }

    sendMessage(MessageType.SIGNIN, MessageAction.EXECUTE, payload);
  };

  useEffect(() => {
    if (!lastMessage) return;

    const parsedData = parseJSON<Message>(lastMessage?.data);

    const type = parsedData?.type;
    const action = parsedData?.action;

    if (type && callbackStore.has(type) && action === MessageAction.NOTIFICATION) {
      callbackStore.get(type)?.forEach((cb) => cb(parsedData));
    }
  }, [callbackStore, lastMessage]);

  return {
    getWebSocket,

    lastMessage,
    lastJsonMessage,

    sendMessage,

    subscribe,
    unsubscribe,

    readyState,
  };
};

export type UseWsReturnType = ReturnType<typeof useWs>;
